import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X, Plus, Search, Check, Loader, Upload,
  FileText, Save, ChevronRight, ChevronLeft, AlertCircle } from "lucide-react";
import axiosInstance, { getUserRole } from "../../API/interceptor";
import RichTextEditor from "@dietary-business-intelligence/textora";
import WorkspaceDropdown from "./WorkspaceDropdown";
import { stripHtml } from "../../utils/sanitizeInput";

/* ══ STYLES ══ */
const iCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none bg-white box-border focus:border-violet-400 focus:ring-2 focus:ring-violet-50 transition-all";

// Cross-browser date+time picker (Firefox fix for datetime-local)
function DateTimePicker({ value, onChange, min, max, className, required }) {
  const date = value ? value.split('T')[0] : '';
  const time = value ? (value.split('T')[1] || '') : '';
  const minDate = min ? min.split('T')[0] : undefined;
  const maxDate = max ? max.split('T')[0] : undefined;
  const handle = (type, val) => {
    if (type === 'date') onChange(val ? `${val}T${time || '00:00'}` : '');
    else onChange(date ? `${date}T${val}` : '');
  };

  return (
    <div className="flex gap-1.5">
      <input type="date" value={date} min={minDate} max={maxDate} required={required}
        onChange={e => handle('date', e.target.value)}
        className={`${className || iCls} flex-[3]`}/>
      <input type="time" value={time}
        onChange={e => handle('time', e.target.value)}
        className={`${className || iCls} flex-[2]`}/>
    </div>
  );
}
const sCls = `${iCls} appearance-none cursor-pointer`;

/* ══ HELPERS ══ */
function fmtLocalStr(localStr) {
  if (!localStr) return "";
  const d = new Date(localStr);
  if (isNaN(d)) return localStr.replace("T", " ");
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function resizeImagesTo25(html) {
  if (!html || !html.includes('<img')) return html;
  try {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('img').forEach(img => {
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.style.width    = '25%';
      img.style.height   = 'auto';
      img.style.maxWidth = '25%';
    });
    return div.innerHTML;
  } catch { return html; }
}

/* Wrapper: forces 25% on pasted images + double-click to preview full size */
function EditorWrapper({ editorNode }) {
  const divRef = useRef(null);
  const obsRef = useRef(null);
  const [previewSrc, setPreviewSrc] = useState(null);

  useEffect(() => {
    const el = divRef.current;
    if (!el) return;

    const applySize = img => {
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.style.width    = '25%';
      img.style.height   = 'auto';
      img.style.maxWidth = '25%';
      img.style.cursor   = 'zoom-in';
    };

    // Intercept image clicks in capture phase — prevents textora's Download/Close toolbar
    const onImgClick = e => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        setPreviewSrc(e.target.src);
      }
    };

    el.addEventListener('click', onImgClick, true);

    obsRef.current = new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') applySize(node);
          node.querySelectorAll?.('img').forEach(applySize);
        });
        if (m.type === 'attributes' && m.target.tagName === 'IMG') applySize(m.target);
      });
    });
    obsRef.current.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['width','height','style'] });

    return () => {
      obsRef.current?.disconnect();
      el.removeEventListener('click', onImgClick, true);
    };
  }, []);

  return (
    <>
      <div ref={divRef} className="border border-gray-200 rounded-lg">{editorNode}</div>
      {previewSrc && createPortal(
        <div onClick={() => setPreviewSrc(null)}
          className="fixed inset-0 z-[999999] bg-black/85 backdrop-blur-[4px] flex items-center justify-center cursor-zoom-out">
          <button onClick={() => setPreviewSrc(null)}
            className="absolute top-4 right-5 w-9 h-9 rounded-full bg-white/20 border-none flex items-center justify-center cursor-pointer text-white">
            <X size={18}/>
          </button>
          <img src={previewSrc} alt="preview" onClick={e => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.5)] cursor-default"/>
        </div>,
        document.body
      )}
    </>
  );
}

function FL({ text, required }) {
  return (
    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
      {text}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}
function F({ label, required, children, err, col="1" }) {
  return (
    <div className={col==="2" ? "col-span-2" : col==="3" ? "col-span-3" : "col-span-1"}>
      {label && <FL text={label} required={required}/>}
      {children}
      {err && <p className="text-red-500 text-[11px] mt-1">{err}</p>}
    </div>
  );
}

/* ══ USER SEARCH DROPDOWN ══ */
function UserSelect({ label, value, onChange, multiple=false, required=false, error=false }) {
  const [open,    setOpen]    = useState(false);
  const [search,  setSearch]  = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debRef   = useRef(null);
  const abortRef = useRef(null);
  const wrapRef  = useRef(null);

  const doSearch = useCallback((term) => {
    clearTimeout(debRef.current);
    abortRef.current?.abort();
    debRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const res = await axiosInstance.get("/task_assignment/get_users_all", {
          params: { q: term || "" },
          signal: abortRef.current.signal,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setResults(data.map(u => ({
          value: String(u.staffid),
          label: u.display_name || u.alias_name || String(u.staffid),
        })));
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setResults([]);
      } finally { setLoading(false); }
    }, 300);
  }, []);

  useEffect(() => {
    if (open) {
      setSearch("");   // clear previous search term
      setResults([]);  // clear stale results immediately
      doSearch("");    // load fresh list
    }
  }, [open]); // eslint-disable-line
  useEffect(() => {
    const fn = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const isSelected = (val) => multiple
    ? (Array.isArray(value) ? value.some(v=>v.value===val) : false)
    : value?.value === val;

  const toggle = (val, lbl) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (arr.some(v=>v.value===val)) onChange(arr.filter(v=>v.value!==val));
      else onChange([...arr, {value:val, label:lbl}]);
    } else {
      onChange({value:val, label:lbl});
      setOpen(false);
    }
  };

  const displayLabel = () => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.length) return <span className="text-slate-400 text-sm">Select...</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {arr.map(v=>(
            <span key={v.value} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 border border-violet-200">
              {v.label}
              <button onClick={e=>{e.stopPropagation(); onChange(arr.filter(x=>x.value!==v.value));}}
                className="cursor-pointer opacity-60 hover:opacity-100 text-xs leading-none border-none bg-transparent">×</button>
            </span>
          ))}
        </div>
      );
    }
    if (value?.label) return <span className="text-sm text-gray-700 font-medium">{value.label}</span>;
    return <span className="text-slate-400 text-sm">Select...</span>;
  };

  return (
    <div ref={wrapRef} className="relative">
      {label && <FL text={label} required={required}/>}
      <div onClick={()=>setOpen(p=>!p)}
        className={`flex flex-wrap items-center gap-1 px-2.5 py-1.5 border rounded-lg min-h-[38px] bg-white cursor-pointer transition-all
          ${error?"border-red-400":open?"border-violet-400 ring-2 ring-violet-50":"border-gray-200 hover:border-violet-300"}`}>
        <div className="flex-1 min-w-0">{displayLabel()}</div>
        <ChevronDown size={13} className={`text-slate-400 flex-shrink-0 transition-transform ${open?"rotate-180":""}`}/>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search size={13} className="text-slate-400 flex-shrink-0"/>
            <input autoFocus value={search}
              onChange={e=>{setSearch(e.target.value);doSearch(e.target.value);}}
              placeholder="Search..." className="border-none outline-none text-sm text-gray-700 w-full bg-transparent"/>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="py-4 text-center"><Loader size={16} className="text-violet-500 animate-spin mx-auto"/></div>
            ) : results.length===0 ? (
              <p className="text-center text-xs text-slate-400 py-4">No users found</p>
            ) : results.map(u => {
              const sel = isSelected(u.value);
              return (
                <div key={u.value} onClick={()=>toggle(u.value,u.label)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${sel?"bg-violet-50":"hover:bg-slate-50"}`}>
                  {multiple && (
                    <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${sel?"bg-violet-600 border-violet-600":"border-gray-300"}`}>
                      {sel && <Check size={10} color="#fff" strokeWidth={3}/>}
                    </div>
                  )}
                  <span className={`text-sm ${sel?"font-semibold text-violet-700":"text-gray-700"}`}>{u.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══ CATEGORY MULTI-SELECT ══ */
function CatSelect({ label, categories, value, onChange }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const fn = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const filtered   = categories.filter(c=>c.toLowerCase().includes(search.toLowerCase()));
  const isSelected = (cat) => value.includes(cat);
  const toggle     = (cat) => {
    if (isSelected(cat)) onChange(value.filter(c=>c!==cat));
    else onChange([...value, cat]);
  };

  return (
    <div ref={wrapRef} className="relative">
      <FL text={label||"Functional Categories"}/>
      <div onClick={()=>setOpen(p=>!p)}
        className={`flex flex-wrap items-center gap-1 px-2.5 py-1.5 border rounded-lg min-h-[38px] bg-white cursor-pointer transition-all
          ${open?"border-violet-400 ring-2 ring-violet-50":"border-gray-200 hover:border-violet-300"}`}>
        <div className="flex-1 min-w-0">
          {value.length===0 ? <span className="text-slate-400 text-sm">Select categories...</span> : (
            <div className="flex flex-wrap gap-1">
              {value.map((cat,i)=>(
                <span key={i} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 border border-violet-200">
                  {cat}
                  <button onClick={e=>{e.stopPropagation();toggle(cat);}} className="cursor-pointer opacity-60 hover:opacity-100 text-xs leading-none border-none bg-transparent">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronDown size={13} className={`text-slate-400 flex-shrink-0 transition-transform ${open?"rotate-180":""}`}/>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search size={13} className="text-slate-400 flex-shrink-0"/>
            <input autoFocus value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search categories..." className="border-none outline-none text-sm text-gray-700 w-full bg-transparent"/>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length===0 ? (
              <p className="text-center text-xs text-slate-400 py-4">No categories found</p>
            ) : filtered.map((cat,i)=>{
              const sel = isSelected(cat);
              return (
                <div key={i} onClick={()=>toggle(cat)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${sel?"bg-violet-50":"hover:bg-slate-50"}`}>
                  <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${sel?"bg-violet-600 border-violet-600":"border-gray-300"}`}>
                    {sel && <Check size={10} color="#fff" strokeWidth={3}/>}
                  </div>
                  <span className={`text-sm ${sel?"font-semibold text-violet-700":"text-gray-700"}`}>{cat}</span>
                </div>
              );
            })}
          </div>
          {value.length>0 && (
            <div className="px-3 py-2 border-t border-gray-100 flex justify-between items-center">
              <span className="text-xs text-slate-400">{value.length} selected</span>
              <button onClick={()=>onChange([])} className="text-xs text-red-500 border-none bg-transparent cursor-pointer">Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══ PRIORITY DROPDOWN ══ */
const PRIORITY_COLORS = {
  URGENT: { bg:"#FEF2F2", color:"#DC2626", border:"#FECACA" },
  HIGH:   { bg:"#FFF7ED", color:"#D97706", border:"#FDE68A" },
  MEDIUM: { bg:"#FEF9C3", color:"#CA8A04", border:"#FDE68A" },
  LOW:    { bg:"#DCFCE7", color:"#16A34A", border:"#BBF7D0" },
};
function PriorityDropdown({ value, onChange, options, error }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const fn = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  const cfg = value ? (PRIORITY_COLORS[value.toUpperCase()] || {}) : {};
  return (
    <div ref={wrapRef} className="relative">
      <div onClick={()=>setOpen(p=>!p)}
        className={`flex items-center justify-between px-2.5 py-2 border rounded-lg min-h-[38px] bg-white cursor-pointer transition-all
          ${error?"border-red-400":open?"border-violet-400 ring-2 ring-violet-50":"border-gray-200 hover:border-violet-300"}`}>
        {value ? (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full border-[1.5px]"
            style={{ background:cfg.bg, color:cfg.color, borderColor:cfg.border }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background:cfg.color }}/>
            {value}
          </span>
        ) : (
          <span className="text-slate-400 text-sm">Select Priority</span>
        )}
        <div className="flex items-center gap-1 ml-auto pl-1 flex-shrink-0">
          {value && (
            <span
              onClick={e=>{ e.stopPropagation(); onChange(""); }}
              className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs cursor-pointer hover:bg-red-50 hover:text-red-400 transition-colors"
            >×</span>
          )}
          <ChevronDown size={13} className={`text-slate-400 transition-transform ${open?"rotate-180":""}`}/>
        </div>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] overflow-hidden">
          {options.map(p => {
            const c = PRIORITY_COLORS[p.toUpperCase()] || {};
            const sel = value === p;
            return (
              <div key={p} onClick={()=>{ onChange(p); setOpen(false); }}
                className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${sel?"bg-violet-50":"hover:bg-slate-50"}`}>
                <div className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center border-2 transition-colors ${sel?"bg-violet-600 border-violet-600":"border-gray-300"}`}>
                  {sel && <div className="w-[6px] h-[6px] rounded-full bg-white"/>}
                </div>
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full border-[1.5px]"
                  style={{ background:c.bg, color:c.color, borderColor:c.border }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background:c.color }}/>
                  {p}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══ STAGE DROPDOWN ══ */
function StageDropdown({ value, onChange, stages }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  useEffect(() => {
    const fn = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  const getName = s => typeof s === "object" ? (s.stage_name || s.name || String(s)) : String(s);
  const filtered = stages.filter(s => getName(s).toLowerCase().includes(search.toLowerCase()));
  return (
    <div ref={wrapRef} className="relative">
      <div onClick={() => { setOpen(p => !p); setSearch(""); }}
        className={`flex items-center justify-between px-2.5 py-2 border rounded-lg min-h-[38px] bg-white cursor-pointer transition-all
          ${open ? "border-violet-400 ring-2 ring-violet-50" : "border-gray-200 hover:border-violet-300"}`}>
        {value ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#EDE9FE] text-[#6D28D9] border-[1.5px] border-[#DDD6FE]">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500"/>
            {value}
          </span>
        ) : (
          <span className="text-slate-400 text-sm">Nothing Selected</span>
        )}
        <ChevronDown size={13} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search size={13} className="text-slate-400 flex-shrink-0"/>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search stage..." className="border-none outline-none text-sm text-gray-700 w-full bg-transparent"/>
          </div>
          <div className="max-h-48 overflow-y-auto">
            <div onClick={() => { onChange(""); setOpen(false); }}
              className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${!value ? "bg-violet-50" : "hover:bg-slate-50"}`}>
              <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${!value ? "bg-violet-600 border-violet-600" : "border-gray-300"}`}>
                {!value && <Check size={10} color="#fff" strokeWidth={3}/>}
              </div>
              <span className="text-sm text-slate-400 italic">Nothing Selected</span>
            </div>
            {filtered.map((s, i) => {
              const name = getName(s);
              const sel = value === name;
              return (
                <div key={i} onClick={() => { onChange(name); setOpen(false); }}
                  className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors ${sel ? "bg-violet-50" : "hover:bg-slate-50"}`}>
                  <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${sel ? "bg-violet-600 border-violet-600" : "border-gray-300"}`}>
                    {sel && <Check size={10} color="#fff" strokeWidth={3}/>}
                  </div>
                  <span className={`text-sm ${sel ? "font-semibold text-violet-700" : "text-gray-700"}`}>{name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══ FILE DROP ZONE ══ */
function FileDropZone({ files, onChange }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();
  const fmtSize = (b) => b>1024*1024?`${(b/1024/1024).toFixed(1)}MB`:`${(b/1024).toFixed(0)}KB`;
  const addFiles = (fl) => {
    const arr = Array.from(fl);
    onChange(prev=>{
      const ex = new Set(prev.map(f=>f.name+f.size));
      return [...prev, ...arr.filter(f=>!ex.has(f.name+f.size))];
    });
  };
  return (
    <div>
      <FL text="Attachment (Multiple Files)"/>
      <div onDragOver={e=>{e.preventDefault();setDragging(true);}}
        onDragLeave={()=>setDragging(false)}
        onDrop={e=>{e.preventDefault();setDragging(false);addFiles(e.dataTransfer.files);}}
        onClick={()=>inputRef.current.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${dragging?"border-violet-500 bg-violet-50":"border-gray-300 bg-slate-50 hover:border-violet-300"}`}>
        <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-2">
          <Upload size={18} color="#7C3AED"/>
        </div>
        <p className="text-sm font-semibold text-gray-700 mb-1">{dragging?"Drop files here!":"Drag & drop files here"}</p>
        <p className="text-xs text-slate-400">or <span className="text-violet-600 font-semibold">click to browse</span></p>
        <p className="text-[10px] text-slate-400 mt-1">.jpg, .jpeg, .png, .pdf, .csv, .xlsx, .docx, .doc, .xls, .ppt, .pptx, .txt, .webp · Max 5MB per file</p>
      </div>
      <input ref={inputRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" className="hidden" onChange={e=>addFiles(e.target.files)}/>
      {files.length>0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {files.map((f,i)=>(
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-gray-100">
              <FileText size={13} color="#7C3AED" className="flex-shrink-0"/>
              <span className="text-xs text-gray-700 flex-1 truncate">{f.name}</span>
              <span className="text-[10px] text-slate-400">{fmtSize(f.size)}</span>
              <button onClick={()=>onChange(prev=>prev.filter((_,j)=>j!==i))}
                className="w-5 h-5 rounded flex items-center justify-center bg-red-50 border-none cursor-pointer flex-shrink-0">
                <X size={9} color="#EF4444"/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══ EMPTY FORM STATE ══ */
const emptyMain = () => ({
  task_type:"", detailedDescription:"", priority:"", stage:"",
  functional_categories:[], start_date:"", completionSLA:"",
  assignedTo:[], created_by:null, reviewer:[], informer:[],
  taskComment:"", completionApproval:false,
  repeat:false, repeatInterval:1, repeatIntervalType:"days", endDate:"",
});

/* ══ MAIN DRAWER ══ */
export default function CreateTaskDrawer({ open, onClose, resumeDraftData=null, onTaskCreated, todoConvert=null }) {
  // ── API data ──
  const [workspaces,   setWorkspaces]   = useState([]);
  const [templates,    setTemplates]    = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [stages,       setStages]       = useState([]);

  // ── Selection ──
  const [workspace,    setWorkspace]    = useState("");
  const [templateId,   setTemplateId]   = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateSubs, setTemplateSubs] = useState([]); // subtask templates from selected template

  // ── Steps (0 = main task, 1..N = subtasks) ──
  const [currentStep,  setCurrentStep]  = useState(0);
  const [savedDraftId, setSavedDraftId] = useState(null);

  // ── Main task form ──
  const [main,    setMain]    = useState(emptyMain());
  const [files,   setFiles]   = useState([]);
  // ── Forces RichTextEditor to re-mount with correct value after template/draft load ──
  const [descKey, setDescKey] = useState(0);
  const [errors,  setErrors]  = useState({});

  // ── Subtask forms (array indexed by subtask index) ──
  const [subForms, setSubForms] = useState([]);

  // ── Current user + TL info for pre-fill ──
  const [currentUser, setCurrentUser] = useState({ userId: "", userName: "", managerId: "", managerName: "" });

  // ── UI state ──
  const [submitting, setSubmitting]  = useState(false);
  const [saving,     setSaving]      = useState(false);
  const [submitted,  setSubmitted]   = useState(false);
  const [toast,      setToast]       = useState(null);

  // ✅ Ref to block template useEffect from overwriting draft data
  const isDraftResuming = useRef(false);

  const setM = (key,val) => setMain(p=>({...p,[key]:val}));
  const setSub = (idx,key,val) => setSubForms(p=>{
    const arr=[...p];
    arr[idx]={...arr[idx],[key]:val};
    return arr;
  });

  const showToast = (msg, type="success") => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),3500);
  };

  // ── Priority list from DB ──
  const [priorityList, setPriorityList] = useState([]);
  useEffect(()=>{
    axiosInstance.post("/task_assignment/task_priority")
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setPriorityList(data.map(p => p.priority).filter(Boolean));
      })
      .catch(()=>{});
  },[]);

  // ── Fetch current user + TL/manager info for pre-fill ──
  // Step 1: get_user_role → manager_id + manager_name (now returned by backend)
  // Step 2: get_users_all → current user's staffid + display_name
  useEffect(() => {
    const up       = JSON.parse(localStorage.getItem("userProfile") || "{}");
    const alias    = (up.alias_name || "").trim().toLowerCase();
    const email    = (up.email      || "").trim().toLowerCase();
    const fullName = `${up.firstname || ""} ${up.lastname || ""}`.trim().toLowerCase();

    if (!alias && !email) return;

    // Step 1: get manager info from get_user_role
    getUserRole()
      .then(roleRes => {
        const rd          = roleRes.data || {};
        const managerId   = rd.manager_id   ? String(rd.manager_id)   : "";
        const managerName = rd.manager_name || "";

        // Step 2: resolve current user's staffid + display_name via search
        const q = alias || email.split("@")[0];
        axiosInstance.get("/task_assignment/get_users_all", { params: { q } })
          .then(res => {
            const users = Array.isArray(res.data) ? res.data : [];
            const norm  = s => (s || "").trim().toLowerCase();
            const me = users.find(u =>
              (alias    && norm(u.alias_name)   === alias)    ||
              (alias    && norm(u.display_name) === alias)    ||
              (fullName && norm(u.display_name).startsWith(fullName)) ||
              (email    && norm(u.email)        === email)
            );
            if (me?.staffid) {
              setCurrentUser({
                userId:      String(me.staffid),
                userName:    me.display_name || me.alias_name || "",
                managerId,
                managerName,
              });
            }
          })
          .catch(() => {});
      })
      .catch(() => {});
  }, []);

  // ── Load workspaces on mount ──
  useEffect(()=>{
    axiosInstance.get("/task_assignment/get_product_codes")
      .then(res=>{
        const arr = Array.isArray(res.data?.product_codes)?res.data.product_codes
                  : Array.isArray(res.data)?res.data:[];
        const ws = arr.map(item=>{
          if (typeof item==="object"){
            const key=Object.keys(item)[0];
            return {code:String(key), name:item[key]};
          }
          return null;
        }).filter(Boolean);
        setWorkspaces(ws);
      }).catch(()=>{});

    axiosInstance.get("/task_assignment/get_functional_categories")
      .then(res=>{ if(Array.isArray(res.data)) setCategories(res.data); })
      .catch(()=>{});
  },[]);

  // ── Load templates when workspace changes ──
  useEffect(()=>{
    if (!workspace){ setTemplates([]); setTemplateId(""); setTemplateName(""); return; }
    axiosInstance.get(`/task_assignment/get_templates/${workspace}`)
      .then(res=>{ setTemplates(Array.isArray(res.data?.templates)?res.data.templates:[]); })
      .catch(()=>setTemplates([]));

    // Load stages for workspace
    axiosInstance.get(`/task_assignment/get_stages_for_create_task/${workspace}`)
      .then(res=>{
        const s = res.data?.stages || res.data || [];
        setStages(Array.isArray(s)?s:[]);
      }).catch(()=>setStages([]));
  },[workspace]);

  // ── Load template details when template selected ──
  useEffect(()=>{
    if (!templateId){ setTemplateSubs([]); return; }

    axiosInstance.get(`/task_assignment/get_template/${templateId}`)
      .then(res=>{
        const tpl = res.data?.template || res.data;
        if (!tpl) return;

        // ✅ Subtask base structure — always set (required for step count)
        const subs = Array.isArray(tpl.subtasks) ? tpl.subtasks : [];
        setTemplateSubs(subs);

        if (isDraftResuming.current) {
          // ── DRAFT RESUME MODE ──
          // Only set subtask structure from template
          // Main form + subtask values are already set by the resume useEffect
          // HTML logic: template se base, draft se override
          setSubForms(prev => subs.map((s, idx) => {
            const draftSub = prev[idx] || {};
            return {
              // Base from template
              task_type:           s.name || s.task_type || "",
              // Draft description override (jo user ne fill kiya)
              detailedDescription: draftSub.detailedDescription || s.detailed_description || "",
              // Draft priority override
              priority:            draftSub.priority || s.priority || "MEDIUM",
              // Draft dates (user filled)
              start_date:          draftSub.start_date  || "",
              completionSLA:       draftSub.completionSLA || "",
              // RACI — draft se, warna template se
              created_by: draftSub.created_by || (s.accountable
                ? { value: String(s.accountable), label: s.accountable_name || String(s.accountable) }
                : null),
              assignedTo: draftSub.assignedTo?.length > 0 ? draftSub.assignedTo : (s.responsible
                ? [{ value: String(s.responsible), label: s.responsible_name || String(s.responsible) }]
                : []),
              reviewer: draftSub.reviewer?.length > 0 ? draftSub.reviewer
                : Array.isArray(s.consulted_resolved) && s.consulted_resolved.length > 0
                  ? s.consulted_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
                  : [],
              informer: draftSub.informer?.length > 0 ? draftSub.informer
                : Array.isArray(s.informer_resolved) && s.informer_resolved.length > 0
                  ? s.informer_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
                  : [],
              completionApproval: draftSub.completionApproval || false,
              subtaskComment:     draftSub.subtaskComment || "",
              file:               null,
            };
          }));
          // ✅ Flag off — ab normal template changes kaam karenge
          isDraftResuming.current = false;
        } else {
          // ── FRESH TEMPLATE SELECT MODE ──
          // Set main task fields from template
          setMain(p=>({
            ...p,
            task_type:             tpl.task_type || tpl.template_name || p.task_type,
            detailedDescription:   tpl.detailed_description || p.detailedDescription,
            priority:              tpl.priority || p.priority,
            stage:                 tpl.current_stage || p.stage,
            functional_categories: Array.isArray(tpl.functional_categories) ? tpl.functional_categories : p.functional_categories,
            created_by: tpl.task_created_by
              ? { value: String(tpl.task_created_by), label: tpl.accountable_name || String(tpl.task_created_by) }
              : p.created_by,
            assignedTo: Array.isArray(tpl.assigned_to)
              ? tpl.assigned_to.map((id, i) => ({
                  value: String(id),
                  label: (tpl.responsible_names || [])[i] || String(id)
                }))
              : p.assignedTo,
            reviewer: Array.isArray(tpl.consulted_resolved) && tpl.consulted_resolved.length > 0
              ? tpl.consulted_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
              : p.reviewer,
            informer: Array.isArray(tpl.informed_resolved) && tpl.informed_resolved.length > 0
              ? tpl.informed_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
              : p.informer,
          }));

          // Force RichTextEditor to re-mount with pre-filled description
          setDescKey(k => k + 1);

          // Subtask forms fresh init
          setSubForms(subs.map(s => ({
            task_type:           s.name || s.task_type || "",
            detailedDescription: s.detailed_description || "",
            priority:            s.priority || "MEDIUM",
            start_date:          "",
            completionSLA:       "",
            assignedTo: s.responsible
              ? [{ value: String(s.responsible), label: s.responsible_name || String(s.responsible) }]
              : [],
            created_by: s.accountable
              ? { value: String(s.accountable), label: s.accountable_name || String(s.accountable) }
              : null,
            reviewer: Array.isArray(s.consulted_resolved) && s.consulted_resolved.length > 0
              ? s.consulted_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
              : Array.isArray(s.consulted)
                ? s.consulted.map(u => ({ value: String(u.id || u), label: u.name || String(u) }))
                : [],
            informer: Array.isArray(s.informer_resolved) && s.informer_resolved.length > 0
              ? s.informer_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
              : Array.isArray(s.informer)
                ? s.informer.map(u => ({ value: String(u.id || u), label: u.name || String(u) }))
                : [],
            completionApproval: false,
            subtaskComment:     "",
            file:               null,
          })));
        }
      }).catch(()=>{});
  },[templateId]);

  // ── Resume draft ──
  useEffect(()=>{
    if (!resumeDraftData) return;
    const d  = resumeDraftData;
    const md = d.main_task_data || {};

    // ✅ Step 1: Flag ON — block the template useEffect
    isDraftResuming.current = true;

    // ✅ Step 2: Set draft subtask data first
    // Template useEffect will see this in prev and keep draft values safe
    if (Array.isArray(d.subtasks_data) && d.subtasks_data.length > 0) {
      setSubForms(d.subtasks_data.map(s => ({
        task_type:           s.name || s.task_type || "",
        detailedDescription: s.detailed_description || "",
        priority:            s.priority || "MEDIUM",
        start_date:          s.start_date || "",
        completionSLA:       s.completion_sla || "",
        created_by: s.accountable
          ? { value: String(s.accountable), label: s.accountable_name || String(s.accountable) }
          : null,
        assignedTo: s.responsible
          ? [{ value: String(s.responsible), label: s.responsible_name || String(s.responsible) }]
          : [],
        reviewer: Array.isArray(s.consulted_resolved) && s.consulted_resolved.length > 0
          ? s.consulted_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
          : [],
        informer: Array.isArray(s.informer_resolved) && s.informer_resolved.length > 0
          ? s.informer_resolved.map(u => ({ value: String(u.id), label: u.name || String(u.id) }))
          : [],
        completionApproval: s.completionApproval || false,
        subtaskComment:     s.comment || "",
        file:               null,
      })));
    }

    // ✅ Step 3: Main form fields restore
    setMain({
      task_type:             md.task_type || "",
      detailedDescription:   md.detailedDescription || md.detailed_description || "",
      priority:              md.priority || "",
      stage:                 md.stage || md.task_stage || "",
      functional_categories: Array.isArray(md.functional_categories) ? md.functional_categories : [],
      start_date:            md.start_date || "",
      completionSLA:         md.completionSLA || md.completion_sla || "",
      completionApproval:    md.completionApproval || false,
      taskComment:           md.taskComment || md.task_comment || "",
      repeat:                md.repeat || false,
      repeatInterval:        parseInt(md.repeatInterval || md.repeat_interval || 1),
      repeatIntervalType:    md.repeatIntervalType || md.repeat_interval_type || "days",
      endDate:               md.endDate || md.end_date || "",
      created_by: md.created_by && typeof md.created_by === "object"
        ? md.created_by
        : md.created_by && md.created_by_name
          ? { value: String(md.created_by), label: md.created_by_name }
          : null,
      assignedTo: Array.isArray(md.assignedTo) && md.assignedTo.length > 0
        ? md.assignedTo
        : Array.isArray(md.assign_to_user_names)
          ? md.assign_to_user_names.map(u => ({ value: String(u.id), label: u.text || String(u.id) }))
          : [],
      reviewer: Array.isArray(md.reviewer) && md.reviewer.length > 0
        ? md.reviewer
        : Array.isArray(md.reviewer_user_names)
          ? md.reviewer_user_names.map(u => ({ value: String(u.id), label: u.text || String(u.id) }))
          : [],
      informer: Array.isArray(md.informer) && md.informer.length > 0
        ? md.informer
        : Array.isArray(md.informer_user_names)
          ? md.informer_user_names.map(u => ({ value: String(u.id), label: u.text || String(u.id) }))
          : [],
    });

    // ✅ Step 4: Draft meta + force editor re-mount with description
    setSavedDraftId(String(d._id || ""));
    setTemplateName(d.template_name || "");
    setCurrentStep(d.current_step || 0);
    setDescKey(k => k + 1);

    // ✅ Step 5: Set Workspace + TemplateId — these will trigger useEffects
    // Workspace useEffect: loads templates + stages (OK)
    // Template useEffect: isDraftResuming=true → structure only, values kept safe
    setWorkspace(String(d.product_code || ""));
    setTemplateId(d.template_id || "");

  },[resumeDraftData]);

  // ── Reset on close ──
  const resetAll = (prefill = {}) => {
    setWorkspace(""); setTemplateId(""); setTemplateName("");
    setTemplateSubs([]); setSubForms([]); setCurrentStep(0);
    setMain({ ...emptyMain(), ...prefill }); setFiles([]); setErrors({});
    setSavedDraftId(null); setSubmitted(false);
    setDescKey(k => k + 1); // force RichTextEditor to re-mount empty
  };

  // Reset form every time the drawer opens fresh (skip if resuming a draft or todo convert)
  useEffect(() => {
    if (open && !resumeDraftData && !todoConvert) {
      const prefill = {};

      // Responsible → always the logged-in user
      if (currentUser.userId && currentUser.userName) {
        prefill.assignedTo = [{ value: currentUser.userId, label: currentUser.userName }];
      }

      // Accountable → TL/manager if exists, else fall back to the user themselves
      if (currentUser.managerId && currentUser.managerName) {
        prefill.created_by = { value: currentUser.managerId, label: currentUser.managerName };
      } else if (currentUser.userId && currentUser.userName) {
        prefill.created_by = { value: currentUser.userId, label: currentUser.userName };
      }

      resetAll(prefill);
    }
  }, [open, resumeDraftData, todoConvert, currentUser]); // eslint-disable-line

  // Pre-fill from Todo List "Convert to Full Task"
  useEffect(() => {
    if (!todoConvert) return;
    resetAll();
    setMain(p => ({
      ...p,
      task_type:             todoConvert.taskName || "",
      functional_categories: Array.isArray(todoConvert.categories) ? todoConvert.categories : [],
      detailedDescription:   todoConvert.details || "",
    }));
    setDescKey(k => k + 1);
  }, [todoConvert]); // eslint-disable-line

  const handleClose = () => { resetAll(); onClose(); };

  // ── Validate current step ──
  const validateStep = (step) => {
    const e={};
    if (step===0){
      if (!workspace)                   e.workspace="Workspace is required";
      if (!main.task_type.trim())       e.task_type="Task name is required";
      if (!main.priority)               e.priority="Priority is required";
      if (!main.completionSLA)          e.completionSLA="Due date is required";
      if (!main.created_by?.value)      e.created_by="Accountable is required";
      if (main.repeat && !main.endDate) e.endDate="End date is required";
    } else {
      const sub = subForms[step-1]||{};
      if (!sub.completionSLA)           e.completionSLA="Due date is required";
    }
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const totalSteps = 1 + (templateSubs.length>0 ? subForms.length : 0);

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep(p=>p+1);
    setErrors({});
  };

  const handleBack = () => { setCurrentStep(p=>p-1); setErrors({}); };

  // ── Save Draft ──
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("template_id",   templateId);
      fd.append("template_name", templateName);
      fd.append("product_code",  workspace);
      fd.append("current_step",  String(currentStep));
      fd.append("step_label",    currentStep===0
        ? `Main Task (Step 1)`
        : `Subtask ${currentStep} of ${subForms.length} (Step ${currentStep+1})`
      );
      // ✅ Save with HTML collectMainTaskData field names — so resume works correctly
      fd.append("main_task_data", JSON.stringify({
        task_type:              stripHtml(main.task_type),
        detailed_description:   main.detailedDescription,
        priority:               main.priority,
        task_stage:             main.stage,
        functional_categories:  main.functional_categories,
        start_date:             main.start_date,
        completion_sla:         main.completionSLA,
        completion_approval:    main.completionApproval,
        task_comment:           main.taskComment,
        repeat:                 main.repeat,
        repeat_interval:        main.repeatInterval,
        repeat_interval_type:   main.repeatIntervalType,
        end_date:               main.endDate,
        // Accountable
        created_by:             main.created_by?.value || "",
        created_by_name:        main.created_by?.label || "",
        // Responsible — [{id, text}] format same as HTML
        assign_to_user_names: main.assignedTo.map(u => ({ id: u.value, text: u.label })),
        // Consulted
        reviewer_user_names:  main.reviewer.map(u => ({ id: u.value, text: u.label })),
        // Informed
        informer_user_names:  main.informer.map(u => ({ id: u.value, text: u.label })),
      }));
      fd.append("subtasks_data", JSON.stringify(
        subForms.map((s,i)=>({
          index:i, name:stripHtml(s.task_type),
          detailed_description:s.detailedDescription,
          priority:s.priority, start_date:s.start_date,
          completion_sla:s.completionSLA,
          accountable:s.created_by?.value||"",
          responsible:s.assignedTo?.[0]?.value||"",
          is_filled:!!(s.completionSLA),
        }))
      ));

      // Append files
      files.forEach((f,i)=>{ fd.append(`main_task_file_${i}`,f); });
      fd.append("main_task_file_count", String(files.length));

      const url = savedDraftId
        ? `/task_assignment/update_template_draft/${savedDraftId}`
        : "/task_assignment/save_template_draft";

      const res = await axiosInstance.post(url, fd, {
        headers:{"Content-Type":"multipart/form-data"}
      });

      if (!savedDraftId && res.data?.draft_id) {
        setSavedDraftId(res.data.draft_id);
      }
      showToast(savedDraftId?"Draft updated successfully":"Draft saved! You can resume later.");
    } catch { showToast("Failed to save draft","error"); }
    finally { setSaving(false); }
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("task_type",             stripHtml(main.task_type));
      fd.append("detailedDescription",   main.detailedDescription);
      fd.append("priority",              main.priority);
      fd.append("product_code",          workspace);
      fd.append("completionSLA",         fmtLocalStr(main.completionSLA));
      fd.append("completionApproval",    main.completionApproval?"1":"0");
      fd.append("repeat",                main.repeat?"1":"0");
      fd.append("repeatInterval",        String(main.repeatInterval));
      fd.append("repeatIntervalType",    main.repeatIntervalType);
      fd.append("taskComment",           main.taskComment);
      fd.append("timezone",              Intl.DateTimeFormat().resolvedOptions().timeZone);
      fd.append("clientLocalTime",       new Date().toLocaleString('sv-SE', {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}));
      if (main.stage)      fd.append("task_stage",   main.stage);
      if (main.start_date) fd.append("start_date",   fmtLocalStr(main.start_date));
      if (main.endDate)    fd.append("endDate",       main.endDate);
      if (main.created_by?.value) fd.append("created_by", main.created_by.value);
      if (todoConvert?.todoId) fd.append("todo_id", todoConvert.todoId);

      main.assignedTo.forEach(u=>fd.append("assignedTo",u.value));
      main.reviewer.forEach(u=>fd.append("reviewer",u.value));
      main.informer.forEach(u=>fd.append("informer",u.value));
      main.functional_categories.forEach(c=>fd.append("functional_categories",c));
      files.forEach(f=>fd.append("attachment",f));

      if (savedDraftId) {
        fd.append("draft_id", savedDraftId);
      }

      // ── Template subtasks ──
      const hasTemplateSubs = subForms.length>0;
      const url = hasTemplateSubs
        ? "/task_assignment/add_task_with_template"
        : "/task_assignment/add_task";

      if (hasTemplateSubs){
        // Validate all subtasks have due date
        for (let i=0;i<subForms.length;i++){
          if (!subForms[i].completionSLA){
            showToast(`Subtask "${subForms[i].task_type||`#${i+1}`}" is missing a due date`,"error");
            setCurrentStep(i+1);
            setSubmitting(false);
            return;
          }
        }
        const subsForSubmit = subForms.map(s=>({
          name:stripHtml(s.task_type),
          detailed_description:s.detailedDescription,
          priority:s.priority,
          assigned_to:s.assignedTo?.[0]?.value||"",
          created_by:s.created_by?.value||"",
          completion_sla:fmtLocalStr(s.completionSLA),
          start_date:s.start_date?fmtLocalStr(s.start_date):"",
          observer:s.reviewer.map(u=>u.value),
          informer:s.informer.map(u=>u.value),
          completionApproval:s.completionApproval?"1":"0",
          comment:s.subtaskComment||"",
        }));
        fd.append("template_subtasks", JSON.stringify(subsForSubmit));
        // Subtask files
        subForms.forEach((s,i)=>{ if(s.file) fd.append(`subtask_file_${i}`,s.file); });
      }

      const res = await axiosInstance.post(url, fd, {
        headers:{"Content-Type":"multipart/form-data"}
      });

      if (res.data?.message || res.data?.success!==false){
        // Auto delete draft after success
        if (savedDraftId){
          axiosInstance.post(`/task_assignment/delete_template_draft/${savedDraftId}`).catch(()=>{});
        }
        setSubmitted(true);
        showToast("Task created successfully!");
        if (onTaskCreated) onTaskCreated();
        setTimeout(()=>{ setSubmitted(false); resetAll(); onClose(); }, 1500);
      } else {
        showToast(res.data?.error||res.data?.message||"Failed to create task","error");
      }
    } catch (e) {
      showToast(e?.response?.data?.error||"Failed to create task. Please try again.","error");
    } finally { setSubmitting(false); }
  };

  /* ── Derived ── */
  const hasTemplate    = !!templateId;
  const isLastStep     = currentStep === totalSteps-1;
  const isMainStep     = currentStep === 0;
  const currentSubIdx  = currentStep-1;
  const currentSub     = !isMainStep && subForms[currentSubIdx] ? subForms[currentSubIdx] : null;

  return (
    <>
      {/* Backdrop */}
      {open && <div onClick={handleClose} className="fixed inset-0 bg-slate-900/35 z-[900] backdrop-blur-sm"/>}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold shadow-xl
          ${toast.type==="error"?"bg-red-500 text-white":"bg-emerald-500 text-white"}`}>
          {toast.type==="error" ? <AlertCircle size={15}/> : <Check size={15}/>}
          {toast.msg}
        </div>
      )}

      <div className={`fixed top-0 right-0 bottom-0 bg-white z-[901] shadow-2xl flex flex-col w-[min(1200px,96vw)] transition-transform duration-300 ease-[cubic-bezier(.4,0,.2,1)]
        ${open?"translate-x-0":"translate-x-full"}`}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-gray-100 bg-slate-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[rgb(124,132,207)] to-[rgb(187,196,255)]">
              <Plus size={16} className="text-white"/>
            </div>
            <div>
              <span className="text-base font-extrabold text-indigo-950">Create New Task</span>
              {/* Step indicator */}
              {hasTemplate && totalSteps>1 && (
                <div className="flex items-center gap-1 mt-0.5">
                  {Array.from({length:totalSteps}).map((_,i)=>(
                    <div key={i} className={`h-1.5 rounded-full transition-all ${
                      i<currentStep?"w-5 bg-emerald-400":
                      i===currentStep?"w-8 bg-violet-500":"w-5 bg-gray-200"
                    }`}/>
                  ))}
                  <span className="text-[10px] text-slate-400 ml-1 font-semibold">
                    Step {currentStep+1} of {totalSteps}
                    {!isMainStep && ` — Subtask ${currentSubIdx+1}: ${currentSub?.task_type||""}`}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Save Draft — only when template selected */}
            {hasTemplate && (
              <button onClick={handleSaveDraft} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-600 text-xs font-semibold cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-60">
                {saving?<Loader size={12} className="animate-spin"/>:<Save size={12}/>}
                {savedDraftId?"Update Draft":"Save Draft"}
              </button>
            )}
            <button onClick={handleClose} className="w-8 h-8 rounded-lg border border-gray-200 bg-white cursor-pointer flex items-center justify-center text-slate-500 hover:bg-gray-50 transition-colors">
              <X size={15}/>
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-5 py-5 flex-1 overflow-y-auto">

          {/* ── STEP 0: MAIN TASK ── */}
          {isMainStep && (
            <>
              {/* Row 1: Workspace + Template + Task Name */}
              <div className="grid grid-cols-3 gap-3.5 mb-3.5">
                <F label="Select Workspace" required err={errors.workspace}>
                  <WorkspaceDropdown
                    single
                    value={workspace ? [workspace] : []}
                    onChange={vals => setWorkspace(vals[vals.length - 1] || "")}
                    options={workspaces.map(w => ({ value: w.code, label: w.name }))}
                  />
                </F>

                {workspace && (
                  <F label="Use Template">
                    <div className="relative">
                      <select className={sCls} value={templateId}
                        onChange={e=>{
                          const tid = e.target.value;
                          const tname = e.target.options[e.target.selectedIndex]?.text||"";
                          setTemplateId(tid);
                          setTemplateName(tid?tname:"");
                          if (!tid){ setTemplateSubs([]); setSubForms([]); }
                        }}>
                        <option value="">-- No Template --</option>
                        {templates.map(t=>(
                          <option key={t._id} value={t._id}>{t.template_name}</option>
                        ))}
                      </select>
                      <ChevronDown size={13} className="text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"/>
                    </div>
                    {hasTemplate && templateSubs.length>0 && (
                      <p className="text-[11px] text-violet-600 mt-1 font-medium">
                        {templateSubs.length} subtask(s) — fill details in next steps
                      </p>
                    )}
                  </F>
                )}

                <F label="Task Name" required err={errors.task_type}>
                  <input className={`${iCls}${errors.task_type?" border-red-400":""}`}
                    placeholder="Enter task name"
                    value={main.task_type} onChange={e=>setM("task_type",e.target.value)}/>
                </F>
              </div>

              {/* Description — RichTextEditor */}
              <div className="mb-3.5">
                <FL text="Detailed Description" required/>
                <EditorWrapper
                  editorNode={
                    <RichTextEditor
                      theme="light"
                      key={`main-${templateId}-${savedDraftId}-${descKey}`}
                      value={main.detailedDescription}
                      onChange={html=>setM("detailedDescription", resizeImagesTo25(html))}
                    />
                  }
                />
              </div>

              {/* Row 3: Priority + Stage + Functional Categories */}
              <div className="grid grid-cols-3 gap-3.5 mb-3.5">
                <F label="Priority" required err={errors.priority}>
                  <PriorityDropdown
                    value={main.priority}
                    onChange={v=>setM("priority",v)}
                    options={priorityList}
                    error={!!errors.priority}
                  />
                </F>

                <F label="Stage">
                  <StageDropdown
                    value={main.stage}
                    onChange={v => setM("stage", v)}
                    stages={stages}
                  />
                </F>

                <CatSelect
                  categories={categories}
                  value={main.functional_categories}
                  onChange={v=>setM("functional_categories",v)}
                />
              </div>

              {/* Row: Start Date + Due Date + Accountable */}
              <div className="grid grid-cols-3 gap-3.5 mb-3.5">
                <F label="Start Date">
                  <DateTimePicker value={main.start_date}
                    min={(() => { const d=new Date(); d.setDate(d.getDate()-1); return d.toISOString().split("T")[0]; })()}
                    onChange={v=>setM("start_date",v)}/>
                </F>
                <F label="Task Due Date" required err={errors.completionSLA}>
                  <DateTimePicker value={main.completionSLA} required
                    className={`${iCls}${errors.completionSLA?" border-red-400":""}`}
                    onChange={v=>setM("completionSLA",v)}/>
                </F>
                <UserSelect label="Accountable" required error={!!errors.created_by}
                  value={main.created_by} onChange={v=>setM("created_by",v)}/>
              </div>

              {/* Row: Responsible + Consulted + Informed */}
              <div className="grid grid-cols-3 gap-3.5 mb-3.5">
                <UserSelect label="Responsible" required value={main.assignedTo} onChange={v=>setM("assignedTo",v)} multiple/>
                <UserSelect label="Consulted" value={main.reviewer} onChange={v=>setM("reviewer",v)} multiple/>
                <UserSelect label="Informed" value={main.informer} onChange={v=>setM("informer",v)} multiple/>
              </div>

              {/* Files */}
              <div className="mb-3.5">
                <FileDropZone files={files} onChange={setFiles}/>
              </div>

              {/* Comment */}
              <div className="mb-3.5">
                <FL text="Add Comment"/>
                <textarea rows={2} placeholder="Add a comment (optional)"
                  value={main.taskComment} onChange={e=>setM("taskComment",e.target.value)}
                  className={`${iCls} resize-none`}/>
              </div>

              {/* Completion Approval */}
              <div className="mb-3.5 flex items-center gap-2.5">
                <input type="checkbox" id="compApproval" checked={main.completionApproval}
                  onChange={e=>setM("completionApproval",e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-violet-600"/>
                <label htmlFor="compApproval" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Completion Approval Required
                </label>
              </div>

              {/* Occurrence toggle */}
              <div className="mb-3.5">
                <FL text="Occurrence"/>
                <div className="flex items-center gap-2.5 mb-3">
                  <div onClick={()=>setM("repeat",!main.repeat)}
                    className={`w-11 h-6 rounded-full cursor-pointer relative flex-shrink-0 transition-all ${main.repeat?"bg-gradient-to-br from-[rgb(124,132,207)] to-[rgb(187,196,255)]": "bg-slate-200"}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${main.repeat?"left-[22px]":"left-[3px]"}`}/>
                  </div>
                  <span className={`text-sm font-medium transition-colors ${main.repeat?"text-violet-600":"text-slate-400"}`}>
                    {main.repeat?"On":"Off"}
                  </span>
                </div>

                {main.repeat && (
                  <div className="grid grid-cols-3 gap-3 p-4 bg-violet-50 rounded-xl border border-violet-100">
                    <F label="Repeat Interval" required>
                      <input type="number" min={1} className={iCls}
                        value={main.repeatInterval}
                        onChange={e=>setM("repeatInterval",parseInt(e.target.value)||1)}/>
                    </F>
                    <F label="Select Range" required>
                      <div className="relative">
                        <select className={sCls} value={main.repeatIntervalType}
                          onChange={e=>setM("repeatIntervalType",e.target.value)}>
                          <option value="days">Days</option>
                          <option value="weeks">Weeks</option>
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                        <ChevronDown size={13} className="text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"/>
                      </div>
                    </F>
                    <F label="End Date" required err={errors.endDate}>
                      <input type="date" className={iCls}
                        value={main.endDate} onChange={e=>setM("endDate",e.target.value)}/>
                    </F>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── STEP 1+: SUBTASK FORMS ── */}
          {!isMainStep && currentSub && (
            <div>
              {/* Subtask header info */}
              <div className="mb-5 p-3 bg-violet-50 rounded-xl border border-violet-100 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-violet-700">{currentSubIdx+1}</span>
                </div>
                <div>
                  <p className="text-xs text-violet-500 font-semibold uppercase tracking-wider">Subtask {currentSubIdx+1} of {subForms.length}</p>
                  <p className="text-sm font-bold text-violet-800">{currentSub.task_type||"Unnamed Subtask"}</p>
                </div>
              </div>

              {/* Subtask name */}
              <div className="mb-3.5">
                <FL text="Subtask Name" required/>
                <input className={iCls} placeholder="Subtask name"
                  value={currentSub.task_type}
                  onChange={e=>setSub(currentSubIdx,"task_type",e.target.value)}/>
              </div>

              {/* Description */}
              <div className="mb-3.5">
                <FL text="Detailed Description"/>
                <EditorWrapper
                  editorNode={
                    <RichTextEditor
                      theme="light"
                      key={`sub-${currentSubIdx}-${templateId}-${savedDraftId}-${descKey}`}
                      value={currentSub.detailedDescription}
                      onChange={html=>setSub(currentSubIdx,"detailedDescription", resizeImagesTo25(html))}
                    />
                  }
                />
              </div>

              {/* Priority + Dates */}
              <div className="grid grid-cols-3 gap-3.5 mb-3.5">
                <F label="Priority" required>
                  <PriorityDropdown
                    value={currentSub.priority}
                    onChange={v=>setSub(currentSubIdx,"priority",v)}
                    options={priorityList}
                  />
                </F>
                <F label="Start Date">
                  <DateTimePicker value={currentSub.start_date}
                    max={main.completionSLA}
                    onChange={v=>setSub(currentSubIdx,"start_date",v)}/>
                </F>
                <F label="Due Date" required err={errors.completionSLA}>
                  <DateTimePicker value={currentSub.completionSLA} required
                    className={`${iCls}${errors.completionSLA?" border-red-400":""}`}
                    min={currentSub.start_date||undefined}
                    max={main.completionSLA}
                    onChange={v=>setSub(currentSubIdx,"completionSLA",v)}/>
                  {main.completionSLA && (
                    <p className="text-[10px] text-slate-400 mt-1">Max: {main.completionSLA?.split("T")[0]} (main task deadline)</p>
                  )}
                </F>
              </div>

              {/* RACI — 3 col grid */}
              <div className="grid grid-cols-3 gap-3.5 mb-3.5">
                <UserSelect label="Accountable" required
                  value={currentSub.created_by}
                  onChange={v=>setSub(currentSubIdx,"created_by",v)}/>
                <UserSelect label="Responsible" required
                  value={currentSub.assignedTo}
                  onChange={v=>setSub(currentSubIdx,"assignedTo",v)} multiple/>
                <UserSelect label="Consulted"
                  value={currentSub.reviewer}
                  onChange={v=>setSub(currentSubIdx,"reviewer",v)} multiple/>
              </div>
              <div className="mb-3.5">
                <UserSelect label="Informed"
                  value={currentSub.informer}
                  onChange={v=>setSub(currentSubIdx,"informer",v)} multiple/>
              </div>

              {/* Comment */}
              <div className="mb-3.5">
                <FL text="Add Comment"/>
                <textarea rows={2} placeholder="Add a comment (optional)"
                  value={currentSub.subtaskComment}
                  onChange={e=>setSub(currentSubIdx,"subtaskComment",e.target.value)}
                  className={`${iCls} resize-none`}/>
              </div>

              {/* Completion Approval */}
              <div className="mb-3.5 flex items-center gap-2.5">
                <input type="checkbox" id={`subApproval_${currentSubIdx}`}
                  checked={currentSub.completionApproval}
                  onChange={e=>setSub(currentSubIdx,"completionApproval",e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-violet-600"/>
                <label htmlFor={`subApproval_${currentSubIdx}`}
                  className="text-sm font-medium text-gray-700 cursor-pointer">
                  Completion Approval Required
                </label>
              </div>

              {/* File for subtask */}
              <div className="mb-3.5">
                <FL text="Attachment"/>
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  className={iCls}
                  onChange={e=>setSub(currentSubIdx,"file",e.target.files[0]||null)}/>
                <p className="text-[10px] text-slate-400 mt-1">.jpg, .jpeg, .png, .pdf, .csv, .xlsx, .docx, .doc, .xls, .ppt, .pptx, .txt, .webp · Max 5MB per file</p>
                {currentSub.file && (
                  <p className="text-xs text-violet-600 mt-1">{currentSub.file.name}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-5 py-3.5 border-t-2 border-gray-100 bg-slate-50 flex items-center justify-between gap-2.5 flex-shrink-0">
          {/* Left — Back */}
          <div>
            {!isMainStep && (
              <button onClick={handleBack}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-slate-500 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-colors">
                <ChevronLeft size={14}/> Back
              </button>
            )}
          </div>

          {/* Right — Cancel + Next/Submit */}
          <div className="flex items-center gap-2.5">
            <button onClick={handleClose}
              className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-slate-500 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-colors">
              Cancel
            </button>

            {/* Next — when not last step */}
            {!isLastStep && (
              <button onClick={handleNext}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl border-none text-white text-sm font-bold cursor-pointer transition-opacity bg-gradient-to-br from-[rgb(124,132,207)] to-[rgb(187,196,255)]">
                Next <ChevronRight size={14}/>
              </button>
            )}

            {/* Submit — on last step */}
            {isLastStep && (
              <button onClick={handleSubmit} disabled={submitting||submitted}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl border-none text-white text-sm font-bold cursor-pointer disabled:opacity-70 transition-opacity bg-[#7c84cf] shadow-[0_3px_12px_rgba(124,132,207,.4)]">
                {submitting ? <><Loader size={13} className="animate-spin"/> Submitting...</>
                : submitted  ? <><Check size={13}/> Done!</>
                :              <><Check size={13}/> Submit Task</>}
              </button>
            )}
          </div>
        </div>

      </div>
    </>
  );
}